// Configurare tag-uri custom care decid dacă o comandă Shopify contează ca "rezervat"
// în calculul de stoc. Tag-urile sunt salvate în `inventory_settings` în Mongo și
// folosite live la fiecare calcul (list_items + analytics_summary).
import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { X, Plus, Loader2, Tag as TagIcon, Save } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

import { getReservationTags, updateReservationTags, autocompleteOrderTags } from "@/lib/inventory";

export function ReservationTagsDialog({ open, onOpenChange, onSaved }) {
  const [tags, setTags] = useState([]);
  const [input, setInput] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    getReservationTags()
      .then((r) => setTags(r.tags || []))
      .catch(() => toast.error("Eroare la încărcarea tag-urilor"))
      .finally(() => setLoading(false));
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      autocompleteOrderTags(input, 20)
        .then((r) => setSuggestions(r.tags || []))
        .catch(() => setSuggestions([]));
    }, 220);
    return () => clearTimeout(t);
  }, [input, open]);

  const addTag = useCallback((rawTag) => {
    const t = (rawTag || "").trim();
    if (!t) return;
    setTags((prev) => {
      if (prev.some((x) => x.toLowerCase() === t.toLowerCase())) return prev;
      return [...prev, t];
    });
    setInput("");
  }, []);

  const removeTag = (t) => {
    setTags((prev) => prev.filter((x) => x !== t));
  };

  const onSave = async () => {
    setSaving(true);
    try {
      const r = await updateReservationTags(tags);
      toast.success(`Salvat ${r.tags.length} tag-uri · rezervările se recalculează automat`);
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la salvare");
    } finally {
      setSaving(false);
    }
  };

  const tagsLower = new Set(tags.map((t) => t.toLowerCase()));
  const filteredSuggestions = (suggestions || []).filter(
    (s) => !tagsLower.has(s.tag.toLowerCase())
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="reservation-tags-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <TagIcon className="h-4 w-4 text-primary" />
            Taguri considerate "rezervat"
          </DialogTitle>
          <DialogDescription>
            Doar comenzile care conțin <strong>cel puțin unul</strong> din tag-urile de mai jos vor intra în
            calculul de stoc rezervat. Modificările se aplică imediat — următorul refresh va folosi noua listă.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
        ) : (
          <div className="space-y-4">
            {/* Selected tags */}
            <div className="flex flex-wrap gap-1.5 min-h-[40px] p-2 rounded-md border border-border bg-secondary/20" data-testid="selected-reservation-tags">
              {tags.length === 0 && (
                <span className="text-xs text-muted-foreground italic self-center px-2">
                  Niciun tag configurat — nicio comandă nu va fi considerată "rezervată".
                </span>
              )}
              {tags.map((t) => (
                <span
                  key={t}
                  className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-primary/10 text-primary border border-primary/20"
                  data-testid={`tag-chip-${t.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {t}
                  <button
                    onClick={() => removeTag(t)}
                    className="hover:bg-primary/20 rounded-full p-0.5"
                    data-testid={`remove-tag-${t.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>

            {/* Add input */}
            <div className="space-y-2">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
                Adaugă tag
              </div>
              <div className="flex gap-2">
                <Input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(input); } }}
                  placeholder="ex: sms confirmed"
                  className="h-9"
                  data-testid="reservation-tag-input"
                />
                <Button
                  size="sm"
                  className="h-9"
                  onClick={() => addTag(input)}
                  disabled={!input.trim()}
                  data-testid="reservation-tag-add"
                >
                  <Plus className="h-3.5 w-3.5 mr-1" /> Adaugă
                </Button>
              </div>

              {/* Autocomplete suggestions from Shopify order tags */}
              {filteredSuggestions.length > 0 && (
                <div className="space-y-1">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
                    Sugestii din comenzile Shopify
                  </div>
                  <div className="flex flex-wrap gap-1 max-h-32 overflow-y-auto">
                    {filteredSuggestions.slice(0, 16).map((s) => (
                      <button
                        key={s.tag}
                        onClick={() => addTag(s.tag)}
                        className="text-[11px] px-2 py-1 rounded-full bg-secondary/40 hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
                        data-testid={`suggestion-tag-${s.tag.toLowerCase().replace(/\s+/g, "-")}`}
                      >
                        <span>{s.tag}</span>
                        <span className="text-[9px] text-muted-foreground/60 tabular-nums">×{s.count}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={saving}>
            Anulează
          </Button>
          <Button
            onClick={onSave}
            disabled={saving || loading}
            data-testid="reservation-tags-save"
          >
            {saving ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1" />}
            Salvează ({tags.length})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
